<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';
import { useIframeSelection } from '../../composables/use-iframe-selection';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import useHighlightsStore, { highlightColors, type HighlightWithText } from '../../stores/highlights';
import useReaderStore from '../../stores/reader';
import useMediaStore from '../../stores/media';
import useUiStore from '../../stores/ui';
import HighlightFormDialog from './HighlightFormDialog.vue';
import HighlightMenuContent from './HighlightMenuContent.vue';
import AnnotationMobileBar from './AnnotationMobileBar.vue';
import AnnotationColorRow from './AnnotationColorRow.vue';
import AnnotationActionButtons from './AnnotationActionButtons.vue';
import type { Highlight, Underline } from '@ilteducation/annotations-types';
import useClickHandler from '../../composables/use-click-handler';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMessageLines, faPlay, faTrash, faXmark } from '@fortawesome/pro-regular-svg-icons';

library.add(faMessageLines, faPlay, faTrash, faXmark);

type Emits = {
  (e: 'highlightCreated'): void;
  (e: 'playFromCfi', cfi: string): void;
  (e: 'update:showEdit', val: boolean): void;
};
const emit = defineEmits<Emits>();

type Props = {
  profileId?: string;
  editHighlight?: HighlightWithText | null;
  editPosition?: { x: number; y: number };
  showEdit?: boolean;
};
const props = defineProps<Props>();
const { showEdit, editPosition, editHighlight } = toRefs(props);

const { t } = useLocale();
const { smAndDown } = useDisplay();
const uiStore = useUiStore();
const { isAnnotationBarVisible } = storeToRefs(uiStore);
const highlightsStore = useHighlightsStore();
const {
  addAnnotation,
  clearSelectedText,
  findOverlappingHighlights,
  isCfiRangeContainedIn,
  updateHighlight,
  removeHighlight,
} = highlightsStore;
const { isDarkTheme, selectedText, selectionPosition, lastUsedAnnotation } = storeToRefs(highlightsStore);
const { bookId } = storeToRefs(useReaderStore());
const { hasMediaAvailable } = storeToRefs(useMediaStore());
const { registerNoMatchCallback, unregisterNoMatchCallback } = useClickHandler();
const { reapply: reapplyIframeSelection } = useIframeSelection();

const displayColors = highlightColors.filter((c) => c.name !== 'red');

const isEditMode = computed(() => !!(showEdit.value && editHighlight.value));

// Overlapping annotation — drives delete button, save target, comment text.
const activeAnnotation = computed((): HighlightWithText | null => {
  if (isEditMode.value) return editHighlight.value ?? null;
  if (!selectedText.value) return null;
  return findOverlappingHighlights(selectedText.value.cfiRange)[0] ?? null;
});

// Color ring pre-selection: only highlight the existing color when the new selection
// is fully contained within the existing annotation. If the user extended beyond it,
// show no pre-selection so it's clear a tap is needed to confirm the merge.
const colorRingAnnotation = computed((): HighlightWithText | null => {
  if (isEditMode.value) return editHighlight.value ?? null;
  if (!activeAnnotation.value || !selectedText.value) return null;
  return isCfiRangeContainedIn(selectedText.value.cfiRange, activeAnnotation.value.range)
    ? activeAnnotation.value
    : null;
});

const showMenu = ref(false);
const showCommentDialog = ref(false);
const isSaving = ref(false);
const isRemoving = ref(false);
const mobileBarRef = ref<InstanceType<typeof AnnotationMobileBar> | null>(null);

// Snapshot captured before opening the comment dialog — browser selection may be gone by then.
type DialogSnapshot = {
  text: string;
  cfiRange: string;
  type: Highlight['type'] | Underline['type'];
  color: string;
  id?: string;
  comment?: string;
};
const dialogSnapshot = ref<DialogSnapshot | null>(null);

const menuTarget = computed((): [number, number] => {
  if (isEditMode.value && editPosition.value) {
    return [editPosition.value.x, editPosition.value.y];
  }
  return selectionPosition.value ? [selectionPosition.value.x, selectionPosition.value.y] : [0, 0];
});

// Sync showMenu with the driving state.
watch(
  [selectedText, showEdit, isEditMode],
  () => {
    showMenu.value = isEditMode.value ? (showEdit.value ?? false) : !!selectedText.value;
  },
  { immediate: true },
);

// Keep uiStore in sync so touch-controls can bypass preventDefault when the bar is open.
watch(showMenu, async (open) => {
  isAnnotationBarVisible.value = open;
  if (!open || !smAndDown.value) return;
  await reapplyIframeSelection();
});

watch(showCommentDialog, (isOpen) => {
  if (!isOpen) {
    dialogSnapshot.value = null;
    if (isEditMode.value) emit('update:showEdit', false);
    clearSelectedText();
  }
});

const handleMenuClose = () => {
  if (!showCommentDialog.value) {
    if (isEditMode.value) {
      emit('update:showEdit', false);
    }
    clearSelectedText();
  }
};

// Dismiss edit mode when user clicks on empty space inside the iframe
const dismissOnIframeOutsideClick = () => {
  if (isEditMode.value) {
    emit('update:showEdit', false);
    clearSelectedText();
  }
};

onMounted(() => registerNoMatchCallback(dismissOnIframeOutsideClick));
onUnmounted(() => unregisterNoMatchCallback(dismissOnIframeOutsideClick));

const createAnnotation = async (color: string, type: Highlight['type'] | Underline['type']) => {
  if (!selectedText.value || !bookId.value) return;
  isSaving.value = true;
  try {
    await addAnnotation(
      { contentId: bookId.value, type, range: selectedText.value.cfiRange, color, comment: undefined },
      props.profileId,
    );
    lastUsedAnnotation.value = { type, color };
    clearSelectedText();
    emit('highlightCreated');
  } catch (error) {
    console.error('Failed to create annotation:', error);
  } finally {
    isSaving.value = false;
  }
};

const selectColor = async (colorName: string) => {
  if (isEditMode.value && editHighlight.value) {
    isSaving.value = true;
    try {
      await updateHighlight({ ...editHighlight.value, type: 'highlight', color: colorName });
      emit('update:showEdit', false);
      clearSelectedText();
    } finally {
      isSaving.value = false;
    }
  } else {
    await createAnnotation(colorName, 'highlight');
  }
};

const selectUnderline = async () => {
  if (isEditMode.value && editHighlight.value) {
    isSaving.value = true;
    try {
      const isCurrentlyUnderline = editHighlight.value.type === 'underline';
      await updateHighlight({
        ...editHighlight.value,
        type: isCurrentlyUnderline ? 'highlight' : 'underline',
        color: isCurrentlyUnderline ? 'yellow' : 'red',
      });
      emit('update:showEdit', false);
      clearSelectedText();
    } finally {
      isSaving.value = false;
    }
  } else {
    await createAnnotation('red', 'underline');
  }
};

const getCommentText = (h: HighlightWithText | null | undefined): string | undefined => {
  const c = h?.comment;
  if (!c || !('type' in c) || c.type !== 'text' || !('content' in c)) return undefined;
  return (c.content as string).trim() || undefined;
};

const commentText = computed(() => getCommentText(activeAnnotation.value) ?? null);

const buildDialogSnapshot = (): DialogSnapshot | null => {
  if (isEditMode.value && editHighlight.value) {
    const h = editHighlight.value;
    return {
      text: h.text,
      cfiRange: h.range,
      type: h.type,
      color: h.color ?? 'yellow',
      id: h.id,
      comment: getCommentText(h),
    };
  }
  if (selectedText.value) {
    const sv = selectedText.value;
    const existing = activeAnnotation.value;
    return {
      text: sv.text,
      cfiRange: sv.cfiRange,
      type: existing?.type ?? lastUsedAnnotation.value.type,
      color: existing?.color ?? lastUsedAnnotation.value.color,
      id: existing?.id,
      comment: getCommentText(existing),
    };
  }
  return null;
};

const openNoteDialog = () => {
  const snapshot = buildDialogSnapshot();
  if (!snapshot) return;
  dialogSnapshot.value = snapshot;
  showMenu.value = false;
  showCommentDialog.value = true;
};

const saveWithNote = async (color: string, type: Highlight['type'] | Underline['type'], comment?: string) => {
  if (!dialogSnapshot.value?.cfiRange) return;
  isSaving.value = true;
  try {
    const commentObj = comment !== undefined ? { type: 'text' as const, content: comment.trim() } : undefined;
    if (isEditMode.value && editHighlight.value) {
      await updateHighlight({ ...editHighlight.value, type, color, comment: commentObj });
    } else if (dialogSnapshot.value.id && activeAnnotation.value) {
      // Editing existing annotation from selection flow — update directly so the
      // comment-preservation fallback in addHighlight doesn't re-add the old comment.
      await updateHighlight({ ...activeAnnotation.value, type, color, comment: commentObj });
    } else {
      if (!bookId.value) return;
      await addAnnotation(
        { contentId: bookId.value, type, range: dialogSnapshot.value.cfiRange, color, comment: commentObj },
        props.profileId,
      );
      lastUsedAnnotation.value = { type, color };
      emit('highlightCreated');
    }
    showCommentDialog.value = false;
  } catch (error) {
    console.error('Failed to save annotation:', error);
  } finally {
    isSaving.value = false;
  }
};

const handleRemoveFromDialog = async () => {
  if (!editHighlight.value) return;
  isRemoving.value = true;
  try {
    await removeHighlight(editHighlight.value);
    showCommentDialog.value = false;
  } finally {
    isRemoving.value = false;
  }
};

const deleteHighlight = async () => {
  const target = editHighlight.value ?? activeAnnotation.value;
  if (!target) return;
  isRemoving.value = true;
  try {
    await removeHighlight(target);
    if (isEditMode.value) emit('update:showEdit', false);
    clearSelectedText();
  } finally {
    isRemoving.value = false;
  }
};

const playFromAnnotation = () => {
  if (isEditMode.value && editHighlight.value) {
    emit('playFromCfi', editHighlight.value.range);
    emit('update:showEdit', false);
    clearSelectedText();
  } else if (selectedText.value) {
    emit('playFromCfi', selectedText.value.cfiRange);
    clearSelectedText();
  }
  showMenu.value = false;
};
</script>

<template>
  <v-menu
    v-if="!smAndDown"
    v-model="showMenu"
    :target="menuTarget"
    location="bottom center"
    :offset="6"
    transition="fade-transition"
    content-class="pa-0 bg-transparent elevation-0"
    :close-on-content-click="false"
    @update:model-value="(v) => !v && handleMenuClose()"
  >
    <v-sheet
      class="annotation-menu-sheet rounded-xl overflow-hidden"
      role="dialog"
      :aria-label="t('book_player.label_highlight_toolbar_title')"
    >
      <v-overlay v-model="isSaving" contained class="align-center justify-center">
        <v-progress-circular indeterminate color="primary" size="32" />
      </v-overlay>

      <HighlightMenuContent>
        <template #colors>
          <AnnotationColorRow
            :display-colors="displayColors"
            :is-dark-theme="isDarkTheme"
            :active-annotation="colorRingAnnotation"
            @select-color="selectColor"
            @select-underline="selectUnderline"
          />
        </template>
        <template #actions>
          <v-list density="compact" bg-color="transparent" base-color="on-inverse-surface">
            <v-list-item
              :title="
                commentText
                  ? t('book_player.label_annotation_edit_comment')
                  : t('book_player.label_annotation_add_comment')
              "
              @click="openNoteDialog"
            >
              <template #prepend>
                <FontAwesomeIcon icon="far fa-message-lines" class="me-3" />
              </template>
            </v-list-item>
            <v-list-item
              v-if="hasMediaAvailable"
              :title="t('book_player.label_play_from_sentence')"
              @click="playFromAnnotation"
            >
              <template #prepend>
                <FontAwesomeIcon icon="far fa-play" class="me-3" />
              </template>
            </v-list-item>
            <v-list-item
              v-if="!!(isEditMode || activeAnnotation)"
              :title="t('button_delete')"
              :disabled="isRemoving"
              @click="deleteHighlight"
            >
              <template #prepend>
                <FontAwesomeIcon v-if="!isRemoving" icon="far fa-trash" class="me-3" />
                <v-progress-circular v-else indeterminate size="16" class="me-3" />
              </template>
            </v-list-item>
            <v-divider color="outline-variant" class="my-1" />
            <v-list-item :title="t('button_cancel')" @click="handleMenuClose">
              <template #prepend>
                <FontAwesomeIcon icon="far fa-xmark" class="me-3" />
              </template>
            </v-list-item>
          </v-list>
        </template>
      </HighlightMenuContent>
    </v-sheet>
  </v-menu>

  <Teleport v-else to="body">
    <Transition name="annotation-bar">
      <AnnotationMobileBar
        v-if="showMenu"
        ref="mobileBarRef"
        class="annotation-bar-fixed"
        :is-saving="isSaving"
        :sentence-preview="selectedText?.text ?? editHighlight?.text ?? undefined"
      >
        <template #colors>
          <AnnotationColorRow
            :display-colors="displayColors"
            :is-dark-theme="isDarkTheme"
            :active-annotation="colorRingAnnotation"
            @select-color="selectColor"
            @select-underline="selectUnderline"
          />
        </template>
        <template #actions>
          <AnnotationActionButtons
            :has-media-available="hasMediaAvailable"
            :show-delete="!!(isEditMode || activeAnnotation)"
            :is-removing="isRemoving"
            :has-comment="!!commentText"
            @play="playFromAnnotation"
            @open-note="openNoteDialog"
            @delete="deleteHighlight"
            @close="handleMenuClose"
          />
        </template>
      </AnnotationMobileBar>
    </Transition>
  </Teleport>

  <span role="status" class="visually-hidden">
    {{
      isSaving ? t('book_player.label_annotation_saving') : isRemoving ? t('book_player.label_annotation_deleting') : ''
    }}
  </span>

  <HighlightFormDialog
    v-model="showCommentDialog"
    :highlight="{
      id: dialogSnapshot?.id,
      text: dialogSnapshot?.text ?? '',
      color: dialogSnapshot?.color ?? 'yellow',
      type: dialogSnapshot?.type ?? 'highlight',
      comment: dialogSnapshot?.comment ? { type: 'text', content: dialogSnapshot.comment } : undefined,
    }"
    :is-saving="isSaving"
    :is-removing="isRemoving"
    @save="saveWithNote"
    @remove="handleRemoveFromDialog"
  />
</template>

<style scoped lang="scss">
.v-sheet.annotation-menu-sheet {
  --highlight-ring-color: rgb(var(--v-theme-on-inverse-surface));
  background-color: rgb(var(--v-theme-inverse-surface), 0.95);
  color: rgb(var(--v-theme-on-inverse-surface));
  border: 1px solid rgb(var(--v-theme-outline-variant));
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  min-width: 200px;
}

// Plain Teleport (no Vuetify overlay) — zero focus management, no focus battle with iframe.
.annotation-bar-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2000;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.annotation-bar-enter-active,
.annotation-bar-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.annotation-bar-enter-from,
.annotation-bar-leave-to {
  transform: translateY(100%);
}
</style>
